import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';

import type {
  BrandEmailTemplateDetailsResponse,
  BrandSummary,
  EmailTemplateDetails,
  EmailTemplateRecipientType,
  EmailTemplateType,
} from '../../models/brand.models';
import { EMAIL_TEMPLATE_LABELS } from '../../models/brand.models';
import { BrandApiService } from '../../services/brand-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type PlaceholderTarget = 'subject' | 'body' | null;

interface PlaceholderOption {
  label: string;
  token: string;
  sample: string;
}

const PLACEHOLDER_OPTIONS: PlaceholderOption[] = [
  { label: 'Recipient Name', token: '{recipientName}', sample: 'Alex Johnson' },
  { label: 'Sender Name', token: '{senderName}', sample: 'Maya Patel' },
  { label: 'Sender Email', token: '{senderEmail}', sample: 'maya@excelhire.com' },
  { label: 'Document Title', token: '{documentTitle}', sample: 'Master Services Agreement.pdf' },
  { label: 'Document Message', token: '{documentMessage}', sample: 'Please review the highlighted commercial terms.' },
  { label: 'Private Message', token: '{privateMessage}', sample: 'Let me know if you want to revise section 4.' },
  { label: 'Expiry Date', token: '{documentExpiryDate}', sample: 'June 25, 2026' },
  { label: 'Signer List', token: '{signerList}', sample: 'Alex Johnson, Priya Shah' },
  { label: 'Reviewer List', token: '{reviewerList}', sample: 'Jordan Lee' },
  { label: 'In Person Signer List', token: '{inPersonSignerList}', sample: 'Taylor Reed' },
  { label: 'Acknowledgement Message', token: '{acknowledgementMessage}', sample: 'Thank you for completing the document.' },
  { label: 'Document Link', token: '{documentLink}', sample: 'https://app.example.com/document/123' },
];

const FONT_SIZE_OPTIONS = [
  { label: '12 px', value: '2' },
  { label: '14 px', value: '3' },
  { label: '16 px', value: '4' },
  { label: '18 px', value: '5' },
] as const;

const stripHtmlToVisibleText = (value: string): string => {
  return value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeComparableHtml = (value: string): string => {
  return value
    .trim()
    .replace(/>\s+</g, '><')
    .replace(/\s{2,}/g, ' ')
    .replace(/&nbsp;/gi, ' ');
};

const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const toPreviewText = (value: string): string => {
  let output = value;
  for (const placeholder of PLACEHOLDER_OPTIONS) {
    output = output.replaceAll(placeholder.token, placeholder.sample);
  }

  return output.replace(/\{([^{}|]+)\|\{[^{}]+\}\}/g, '$1');
};

@Component({
  selector: 'app-brand-email-template-detail',
  imports: [CommonModule, FormsModule],
  templateUrl: './brand-email-template-detail.component.html',
  styleUrl: './brand-email-template-detail.component.scss',
})
export class BrandEmailTemplateDetailComponent implements OnInit {
  readonly placeholderOptions = PLACEHOLDER_OPTIONS;
  readonly fontSizeOptions = FONT_SIZE_OPTIONS;

  brand: BrandSummary | null = null;
  templateType: EmailTemplateType | null = null;
  details: BrandEmailTemplateDetailsResponse | null = null;

  selectedRecipientType: EmailTemplateRecipientType | null = null;
  draftSubject = '';
  draftHtmlBody = '<p></p>';
  loadedSubject = '';
  loadedHtmlBody = '<p></p>';

  loading = false;
  saving = false;
  resetting = false;
  errorMessage = '';
  subjectErrorMessage = '';
  bodyErrorMessage = '';

  placeholderMenuTarget: PlaceholderTarget = null;
  placeholderQuery = '';
  subjectTriggerStart = 0;
  subjectTriggerEnd = 0;
  bodyTriggerRange: Range | null = null;
  bodyTriggerLength = 0;

  activeFontSize: string = FONT_SIZE_OPTIONS[2].value;
  activeTextColor = '#13263d';

  @ViewChild('subjectInput') private subjectInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bodyEditor')
  set bodyEditorRef(value: ElementRef<HTMLDivElement> | undefined) {
    this.bodyEditor = value;
    this.syncBodyEditorIfNeeded();
  }

  private readonly brandsApi = inject(BrandApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly documentRef = inject(DOCUMENT);

  private brandId = '';
  private shouldSyncBodyEditor = false;
  private bodyEditor?: ElementRef<HTMLDivElement>;

  get templateLabel(): string {
    if (!this.templateType) {
      return 'Email Template';
    }

    return EMAIL_TEMPLATE_LABELS[this.templateType];
  }

  get editorTitle(): string {
    return `Edit ${this.templateLabel} Template`;
  }

  get hasMultipleRecipientTypes(): boolean {
    return (this.details?.recipientTypes.length ?? 0) > 1;
  }

  get currentDetail(): EmailTemplateDetails | null {
    if (!this.details || !this.selectedRecipientType) {
      return null;
    }

    return (
      this.details.emailTemplateDetailsList.find((detail) => detail.recipientType === this.selectedRecipientType) ??
      null
    );
  }

  get currentStatus(): string {
    return this.currentDetail?.status ?? 'Default';
  }

  get isDirty(): boolean {
    return (
      this.draftSubject.trim() !== this.loadedSubject.trim() ||
      normalizeComparableHtml(this.draftHtmlBody) !== normalizeComparableHtml(this.loadedHtmlBody)
    );
  }

  get filteredPlaceholderOptions(): PlaceholderOption[] {
    const query = this.placeholderQuery.trim().toLowerCase();
    if (!query) {
      return this.placeholderOptions;
    }

    return this.placeholderOptions.filter((option) => {
      return (
        option.label.toLowerCase().includes(query) ||
        option.token.toLowerCase().includes(query)
      );
    });
  }

  get previewSubject(): string {
    return toPreviewText(this.draftSubject.trim() || 'Untitled email');
  }

  get previewFromName(): string {
    const brandName = this.brand?.emailDisplayName?.trim() || this.brand?.name?.trim();
    return brandName || 'Your Brand';
  }

  get previewBrandInitials(): string {
    const source = this.brand?.name?.trim() || this.templateLabel;
    return source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  get previewRecipientLabel(): string {
    switch (this.selectedRecipientType) {
      case 'Sender':
        return 'maya@excelhire.com';
      case 'Cc':
        return 'legal-team@example.com';
      default:
        return 'alex.johnson@example.com';
    }
  }

  get previewBodyHtml(): string {
    const buttonStyle = [
      `background:${this.brand?.primaryButtonColor || '#0f62fe'}`,
      `color:${this.brand?.primaryButtonTextColor || '#ffffff'}`,
      'display:inline-block',
      'padding:12px 24px',
      'border-radius:6px',
      'font-weight:600',
      'text-decoration:none',
    ].join(';');

    let output = this.draftHtmlBody;
    output = output.replace(/\{([^{}|]+)\|\{[^{}]+\}\}/g, (_full, label: string) => {
      return `<p style="text-align:center;"><span class="preview-cta" style="${buttonStyle}">${escapeHtml(
        toPreviewText(label.trim()),
      )}</span></p>`;
    });

    for (const placeholder of PLACEHOLDER_OPTIONS) {
      output = output.replaceAll(
        placeholder.token,
        `<span class="preview-token">${escapeHtml(placeholder.sample)}</span>`,
      );
    }

    return output;
  }

  ngOnInit(): void {
    const brandId = this.route.snapshot.paramMap.get('brandId');
    const templateTypeParam = this.route.snapshot.paramMap.get('templateType');

    if (!brandId || !templateTypeParam || !(templateTypeParam in EMAIL_TEMPLATE_LABELS)) {
      this.errorMessage = 'Email template not found.';
      return;
    }

    this.brandId = brandId;
    this.templateType = templateTypeParam as EmailTemplateType;
    this.loadTemplateDetails();
  }

  goBack(): void {
    if (!this.brand) {
      void this.router.navigate(['/settings/branding']);
      return;
    }

    void this.router.navigate(['/settings/branding', this.brand.brandId, 'edit']);
  }

  onRecipientTypeChange(value: string): void {
    this.selectedRecipientType = value as EmailTemplateRecipientType;
    this.applyLoadedDetailToDraft(this.selectedRecipientType);
    this.clearValidationMessages();
  }

  onSubjectSelectionChange(): void {
    const input = this.subjectInput?.nativeElement;
    if (!input) {
      return;
    }

    this.subjectTriggerStart = input.selectionStart ?? 0;
    this.subjectTriggerEnd = input.selectionEnd ?? this.subjectTriggerStart;
  }

  onSubjectKeyup(): void {
    const input = this.subjectInput?.nativeElement;
    if (!input) {
      return;
    }

    this.onSubjectSelectionChange();
    const cursor = input.selectionStart ?? 0;
    const query = this.extractPlaceholderQuery(input.value.slice(0, cursor));
    if (query === null) {
      this.closePlaceholderMenu();
      return;
    }

    this.placeholderMenuTarget = 'subject';
    this.placeholderQuery = query;
    this.subjectTriggerStart = cursor - query.length - 1;
    this.subjectTriggerEnd = cursor;
  }

  onBodyInput(): void {
    this.draftHtmlBody = this.readBodyEditorHtml();
    this.bodyErrorMessage = '';
  }

  onBodyKeyup(): void {
    const editor = this.bodyEditor?.nativeElement;
    if (!editor) {
      return;
    }

    const selection = this.documentRef.defaultView?.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.closePlaceholderMenu();
      return;
    }

    const activeRange = selection.getRangeAt(0);
    const preCaretRange = activeRange.cloneRange();
    preCaretRange.selectNodeContents(editor);
    preCaretRange.setEnd(activeRange.endContainer, activeRange.endOffset);

    const query = this.extractPlaceholderQuery(preCaretRange.toString());
    if (query === null) {
      this.closePlaceholderMenu();
      return;
    }

    this.placeholderMenuTarget = 'body';
    this.placeholderQuery = query;
    this.bodyTriggerRange = activeRange.cloneRange();
    this.bodyTriggerLength = query.length + 1;
  }

  applyBodyCommand(command: string, value?: string): void {
    this.focusBodyEditor();
    (this.documentRef as Document & {
      execCommand?: (name: string, showDefaultUi?: boolean, value?: string) => boolean;
    }).execCommand?.(command, false, value);
    this.draftHtmlBody = this.readBodyEditorHtml();
    this.cdr.markForCheck();
  }

  applyFontSize(value: string): void {
    this.activeFontSize = value;
    this.applyBodyCommand('fontSize', value);
  }

  applyTextColor(value: string): void {
    this.activeTextColor = value;
    this.applyBodyCommand('foreColor', value);
  }

  insertPlaceholder(option: PlaceholderOption): void {
    if (this.placeholderMenuTarget === 'subject') {
      const before = this.draftSubject.slice(0, this.subjectTriggerStart);
      const after = this.draftSubject.slice(this.subjectTriggerEnd);
      this.draftSubject = `${before}${option.token}${after}`;

      queueMicrotask(() => {
        const input = this.subjectInput?.nativeElement;
        if (!input) {
          return;
        }

        const cursor = before.length + option.token.length;
        input.focus();
        input.setSelectionRange(cursor, cursor);
      });
    }

    if (this.placeholderMenuTarget === 'body') {
      this.focusBodyEditor();
      const selection = this.documentRef.defaultView?.getSelection();
      const range = this.bodyTriggerRange;
      if (selection && range) {
        selection.removeAllRanges();
        selection.addRange(range);

        const insertionRange = selection.getRangeAt(0);
        if (insertionRange.startContainer.nodeType === Node.TEXT_NODE) {
          const textNode = insertionRange.startContainer;
          const startOffset = Math.max(0, insertionRange.startOffset - this.bodyTriggerLength);
          insertionRange.setStart(textNode, startOffset);
          insertionRange.deleteContents();
          const placeholderNode = this.documentRef.createTextNode(option.token);
          insertionRange.insertNode(placeholderNode);
          insertionRange.setStartAfter(placeholderNode);
          insertionRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(insertionRange);
        } else {
          const placeholderNode = this.documentRef.createTextNode(option.token);
          insertionRange.insertNode(placeholderNode);
          insertionRange.setStartAfter(placeholderNode);
          insertionRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(insertionRange);
        }
      }

      this.draftHtmlBody = this.readBodyEditorHtml();
    }

    this.closePlaceholderMenu();
    this.clearValidationMessages();
    this.cdr.markForCheck();
  }

  saveChanges(): void {
    this.clearValidationMessages();

    const subject = this.draftSubject.trim();
    const htmlBody = this.readBodyEditorHtml();
    const visibleBody = stripHtmlToVisibleText(htmlBody);

    if (!subject) {
      this.subjectErrorMessage = 'Email subject is required.';
    }

    if (!visibleBody) {
      this.bodyErrorMessage = 'Email body is required.';
    }

    if (this.subjectErrorMessage || this.bodyErrorMessage || !this.templateType || !this.selectedRecipientType) {
      this.cdr.markForCheck();
      return;
    }

    this.saving = true;
    this.feedback.clearError();
    this.brandsApi
      .updateBrandEmailTemplateDetails(this.brandId, {
        emailTemplateType: this.templateType,
        recipientType: this.selectedRecipientType,
        locale: 'EN',
        emailSubject: subject,
        htmlBody,
      })
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.errorMessage = '';
          this.applyDetailsResponse(response, this.selectedRecipientType);
          this.feedback.clearError();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to save template changes. Please try again.',
          }).message;
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Unable to save template changes. Please try again.',
          });
          this.cdr.markForCheck();
        },
      });
  }

  resetToDefault(): void {
    if (!this.templateType || !this.selectedRecipientType) {
      return;
    }

    this.resetting = true;
    this.feedback.clearError();
    this.brandsApi
      .resetBrandEmailTemplateDetails(this.brandId, {
        emailTemplateType: this.templateType,
        recipientType: this.selectedRecipientType,
        locale: 'EN',
      })
      .pipe(
        finalize(() => {
          this.resetting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.errorMessage = '';
          this.applyDetailsResponse(response, this.selectedRecipientType);
          this.feedback.clearError();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to reset this template. Please try again.',
          }).message;
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Unable to reset this template. Please try again.',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadTemplateDetails(): void {
    if (!this.templateType) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    forkJoin({
      brand: this.brandsApi.getBrand(this.brandId),
      details: this.brandsApi.getBrandEmailTemplateDetails(this.brandId, {
        emailTemplateType: this.templateType,
        locale: 'EN',
        isInitialLoad: true,
      }),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ brand, details }) => {
          this.brand = brand;
          this.applyDetailsResponse(details, this.selectedRecipientType);
          this.feedback.clearError();
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load email template details. Please try again.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private applyDetailsResponse(
    response: BrandEmailTemplateDetailsResponse,
    preferredRecipient: EmailTemplateRecipientType | null,
  ): void {
    this.details = response;

    const selectedRecipient =
      (preferredRecipient && response.recipientTypes.includes(preferredRecipient) && preferredRecipient) ||
      response.recipientTypes[0] ||
      null;

    this.selectedRecipientType = selectedRecipient;
    this.applyLoadedDetailToDraft(selectedRecipient);
  }

  private applyLoadedDetailToDraft(recipientType: EmailTemplateRecipientType | null): void {
    const detail =
      recipientType && this.details
        ? this.details.emailTemplateDetailsList.find((entry) => entry.recipientType === recipientType) ?? null
        : null;

    this.loadedSubject = detail?.emailSubject ?? '';
    this.loadedHtmlBody = detail?.htmlBody ?? '<p></p>';
    this.draftSubject = this.loadedSubject;
    this.draftHtmlBody = this.loadedHtmlBody;
    this.shouldSyncBodyEditor = true;
    queueMicrotask(() => this.syncBodyEditorIfNeeded());
    this.closePlaceholderMenu();
    this.clearValidationMessages();
  }

  private syncBodyEditorIfNeeded(): void {
    if (!this.shouldSyncBodyEditor || !this.bodyEditor) {
      return;
    }

    this.bodyEditor.nativeElement.innerHTML = this.draftHtmlBody || '<p></p>';
    this.shouldSyncBodyEditor = false;
  }

  private focusBodyEditor(): void {
    this.bodyEditor?.nativeElement.focus();
  }

  private readBodyEditorHtml(): string {
    const html = this.bodyEditor?.nativeElement.innerHTML?.trim() ?? this.draftHtmlBody.trim();
    return html || '<p></p>';
  }

  private extractPlaceholderQuery(value: string): string | null {
    const match = value.match(/(?:\/|@)([a-zA-Z]*)$/);
    return match ? match[1] : null;
  }

  private closePlaceholderMenu(): void {
    this.placeholderMenuTarget = null;
    this.placeholderQuery = '';
    this.bodyTriggerRange = null;
    this.bodyTriggerLength = 0;
  }

  private clearValidationMessages(): void {
    this.subjectErrorMessage = '';
    this.bodyErrorMessage = '';
  }
}
